import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ArrowLeft, 
  Calendar, 
  Phone, 
  Mail, 
  MapPin, 
  User, 
  Plus,
  Edit,
  Trash2,
  Stethoscope,
  Clock,
  Euro
} from 'lucide-react';
import { patientsApi, interventionsApi, appointmentsApi } from '../services/api';
import { Patient, Intervention, Appointment } from '../types';
import InterventionModal from '../components/InterventionModal';

const PatientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isInterventionModalOpen, setIsInterventionModalOpen] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState<Intervention | null>(null);
  const [activeTab, setActiveTab] = useState<'appointments' | 'interventions'>('appointments');

  const queryClient = useQueryClient();

  // Fetch patient data
  const { data: patient, isLoading: patientLoading } = useQuery(
    ['patient', id],
    async () => {
      const res = await patientsApi.getById(id!);
      return res.data as Patient;
    },
    {
      enabled: !!id,
    }
  );

  // Fetch patient history
  const { data: history, isLoading: historyLoading } = useQuery(
    ['patient-history', id],
    async () => {
      const res = await patientsApi.getHistory(id!);
      return res.data as any;
    },
    {
      enabled: !!id,
    }
  );

  // Fetch interventions
  const { data: interventionsResponse } = useQuery(
    ['patient-interventions', id],
    () => interventionsApi.getByPatient(id!),
    {
      enabled: !!id,
    }
  );

  const interventions = interventionsResponse?.data?.interventions || [];

  // Fetch all appointments and filter client-side for this patient
  const { data: allAppointmentsResponse, isLoading: appointmentsLoading } = useQuery(
    ['all-appointments-for-filtering'],
    () => appointmentsApi.getAll(),
    {
      enabled: !!id,
    }
  );

  // Filter and sort scheduled appointments client-side
  const scheduledAppointments = React.useMemo(() => {
    // The API returns { appointments: [...], totalPages, currentPage, total }
    // axios wraps it in { data: ... }
    const allAppointments = allAppointmentsResponse?.data?.appointments || [];
    console.log('All appointments:', allAppointments); // Debug log
    console.log('Looking for patient ID:', id); // Debug log
    console.log('Appointments array length:', allAppointments.length); // Debug log
    
    // Filter by patient ID first
    const patientAppointments = allAppointments.filter((appointment: any) => {
      const appointmentPatientId = typeof appointment.patient === 'string' 
        ? appointment.patient 
        : appointment.patient?._id;
      console.log('Appointment patient ID:', appointmentPatientId, 'vs target:', id); // Debug log
      
      // Convert both to strings and compare
      const patientIdStr = String(appointmentPatientId);
      const targetIdStr = String(id);
      const matches = patientIdStr === targetIdStr;
      console.log('ID comparison:', patientIdStr, '===', targetIdStr, '?', matches); // Debug log
      
      return matches;
    });
    console.log('Patient appointments:', patientAppointments); // Debug log
    
    // Then filter by status (scheduled or confirmed)
    const filtered = patientAppointments.filter((appointment: any) => 
      appointment.status === 'scheduled' || appointment.status === 'confirmed'
    );
    console.log('Filtered scheduled appointments for patient:', filtered); // Debug log
    
    return filtered.sort((a: any, b: any) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
  }, [allAppointmentsResponse, id]);

  // Debug effect to log appointment data
  React.useEffect(() => {
    console.log('Patient ID from URL:', id);
    if (allAppointmentsResponse) {
      console.log('Raw appointment response:', allAppointmentsResponse);
      console.log('Scheduled appointments calculated:', scheduledAppointments);
    }
  }, [allAppointmentsResponse, id, scheduledAppointments]);

  // Create intervention mutation
  const createInterventionMutation = useMutation(
    (data: Partial<Intervention>) => interventionsApi.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['patient-interventions', id]);
        queryClient.invalidateQueries(['patient-history', id]);
        // Also invalidate dashboard-level lists/stats so other views reflect changes
        queryClient.invalidateQueries(['recent-interventions']);
        queryClient.invalidateQueries(['interventions-stats']);
        setIsInterventionModalOpen(false);
        setSelectedIntervention(null);
      },
    }
  );

  // Update intervention mutation
  const updateInterventionMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Intervention> }) =>
      interventionsApi.update(id, data),
    {
      onSuccess: () => {
          queryClient.invalidateQueries(['patient-interventions', id]);
          queryClient.invalidateQueries(['patient-history', id]);
          queryClient.invalidateQueries(['recent-interventions']);
          queryClient.invalidateQueries(['interventions-stats']);
        setIsInterventionModalOpen(false);
        setSelectedIntervention(null);
      },
    }
  );

  // Delete intervention mutation
  const deleteInterventionMutation = useMutation(
    (id: string) => interventionsApi.delete(id),
    {
      onSuccess: () => {
          queryClient.invalidateQueries(['patient-interventions', id]);
          queryClient.invalidateQueries(['patient-history', id]);
          queryClient.invalidateQueries(['recent-interventions']);
          queryClient.invalidateQueries(['interventions-stats']);
      },
    }
  );

  const handleCreateIntervention = () => {
    setSelectedIntervention(null);
    setIsInterventionModalOpen(true);
  };

  const handleEditIntervention = (intervention: Intervention) => {
    setSelectedIntervention(intervention);
    setIsInterventionModalOpen(true);
  };

  const handleDeleteIntervention = async (intervention: Intervention) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar esta intervención?`)) {
      await deleteInterventionMutation.mutateAsync(intervention._id);
    }
  };

  const handleSaveIntervention = async (data: Partial<Intervention>) => {
    if (selectedIntervention) {
      await updateInterventionMutation.mutateAsync({
        id: selectedIntervention._id,
        data,
      });
    } else {
      await createInterventionMutation.mutateAsync({
        ...data,
        patient: id,
      });
    }
  };

  const handleModalClose = () => {
    setIsInterventionModalOpen(false);
    setSelectedIntervention(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const getInterventionTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      'cleaning': 'Limpieza',
      'filling': 'Empaste',
      'extraction': 'Extracción',
      'root_canal': 'Endodoncia',
      'crown': 'Corona',
      'bridge': 'Puente',
      'implant': 'Implante',
      'orthodontics': 'Ortodoncia',
      'whitening': 'Blanqueamiento',
      'consultation': 'Consulta',
      'emergency': 'Emergencia',
      'other': 'Otro',
    };
    return types[type] || type;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'planned': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (patientLoading || historyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Paciente no encontrado</h2>
        <Link to="/patients" className="text-dental-600 hover:text-dental-700">
          Volver a la lista de pacientes
        </Link>
      </div>
    );
  }

  const appointments = history?.appointments || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/patients"
            className="text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {patient.firstName} {patient.lastName}
            </h1>
            <p className="text-gray-600">Historial del paciente</p>
          </div>
        </div>
        <button
          onClick={handleCreateIntervention}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dental-600 hover:bg-dental-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva Intervención
        </button>
      </div>

      {/* Patient Info Card */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Información del Paciente</h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-center">
              <User className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-500">Edad</p>
                <p className="text-sm text-gray-900">{getAge(patient.dateOfBirth)} años</p>
              </div>
            </div>
            <div className="flex items-center">
              <Mail className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="text-sm text-gray-900">{patient.email}</p>
              </div>
            </div>
            <div className="flex items-center">
              <Phone className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-500">Teléfono</p>
                <p className="text-sm text-gray-900">{patient.phone}</p>
              </div>
            </div>
            {patient.address?.city && (
              <div className="flex items-center">
                <MapPin className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Ciudad</p>
                  <p className="text-sm text-gray-900">{patient.address.city}</p>
                </div>
              </div>
            )}
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-500">Última Visita</p>
                <p className="text-sm text-gray-900">
                  {patient.dentalHistory?.lastVisit 
                    ? formatDate(patient.dentalHistory.lastVisit)
                    : 'Nunca'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Stethoscope className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-500">Total Visitas</p>
                <p className="text-sm text-gray-900">{patient.dentalHistory?.totalVisits || 0}</p>
              </div>
            </div>
          </div>

          {patient.medicalHistory && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Historial Médico</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {patient.medicalHistory.allergies && patient.medicalHistory.allergies.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Alergias</p>
                    <p className="text-sm text-gray-900">{patient.medicalHistory.allergies.join(', ')}</p>
                  </div>
                )}
                {patient.medicalHistory.medications && patient.medicalHistory.medications.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Medicamentos</p>
                    <p className="text-sm text-gray-900">{patient.medicalHistory.medications.join(', ')}</p>
                  </div>
                )}
                {patient.medicalHistory.conditions && patient.medicalHistory.conditions.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Condiciones</p>
                    <p className="text-sm text-gray-900">{patient.medicalHistory.conditions.join(', ')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {patient.emergencyContact && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Contacto de Emergencia</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Nombre</p>
                  <p className="text-sm text-gray-900">{patient.emergencyContact.name || 'No especificado'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Teléfono</p>
                  <p className="text-sm text-gray-900">{patient.emergencyContact.phone || 'No especificado'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Relación</p>
                  <p className="text-sm text-gray-900">{patient.emergencyContact.relationship || 'No especificado'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('appointments')}
              className={`py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'appointments'
                  ? 'border-dental-500 text-dental-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Calendar className="h-4 w-4 inline mr-2" />
              Citas Pendientes ({appointmentsLoading ? '...' : scheduledAppointments.length})
            </button>
            <button
              onClick={() => setActiveTab('interventions')}
              className={`py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'interventions'
                  ? 'border-dental-500 text-dental-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Stethoscope className="h-4 w-4 inline mr-2" />
              Intervenciones ({interventions.length})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'appointments' && (
            <div className="space-y-4">
              {appointmentsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dental-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Cargando citas...</p>
                </div>
              ) : scheduledAppointments.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No hay citas pendientes</p>
                  <p className="text-sm text-gray-400 mt-1">Las citas programadas y confirmadas aparecerán aquí</p>
                </div>
              ) : (
                scheduledAppointments.map((appointment: any) => (
                  <div key={appointment._id} className={`border rounded-lg p-4 transition-colors ${
                    new Date(appointment.startTime).getTime() - new Date().getTime() < 7 * 24 * 60 * 60 * 1000
                      ? 'border-dental-300 bg-dental-50' 
                      : 'border-gray-200 hover:border-dental-300'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="text-lg font-medium text-gray-900">{appointment.title}</h4>
                          {new Date(appointment.startTime).getTime() - new Date().getTime() < 7 * 24 * 60 * 60 * 1000 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              Próxima
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            appointment.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                            appointment.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {appointment.status === 'confirmed' ? 'Confirmada' : 
                             appointment.status === 'scheduled' ? 'Programada' : appointment.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{appointment.description}</p>
                        <div className="flex items-center mt-2 space-x-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(appointment.startTime)} - {new Date(appointment.startTime).toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <User className="h-4 w-4 mr-1" />
                            Dr. {appointment.dentist}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Stethoscope className="h-4 w-4 mr-1" />
                            <span className="capitalize">{appointment.type || 'Consulta'}</span>
                          </div>
                        </div>
                        {appointment.room && (
                          <div className="flex items-center mt-1 text-sm text-gray-500">
                            <MapPin className="h-4 w-4 mr-1" />
                            {appointment.room}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/appointments/${appointment._id}`}
                          className="text-dental-600 hover:text-dental-900 p-2 hover:bg-dental-50 rounded-md transition-colors"
                          title="Ver detalles de la cita"
                        >
                          <Calendar className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'interventions' && (
            <div className="space-y-4">
              {interventions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay intervenciones registradas</p>
              ) : (
                interventions.map((intervention: Intervention) => (
                  <div key={intervention._id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="text-lg font-medium text-gray-900">
                            {getInterventionTypeLabel(intervention.type)}
                          </h4>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(intervention.status)}`}>
                            {intervention.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{intervention.description}</p>
                        <p className="text-sm text-gray-500 mt-1">{intervention.procedure}</p>
                        <div className="flex items-center mt-2 space-x-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(intervention.createdAt)}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Stethoscope className="h-4 w-4 mr-1" />
                            {intervention.dentist}
                          </div>
                          {intervention.cost && (
                            <div className="flex items-center text-sm text-gray-500">
                              <Euro className="h-4 w-4 mr-1" />
                              {intervention.cost}€
                            </div>
                          )}
                          {intervention.duration && (
                            <div className="flex items-center text-sm text-gray-500">
                              <Clock className="h-4 w-4 mr-1" />
                              {intervention.duration} min
                            </div>
                          )}
                        </div>
                        {intervention.tooth && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                              Diente: {intervention.tooth.number} {intervention.tooth.surface && `- ${intervention.tooth.surface}`}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEditIntervention(intervention)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteIntervention(intervention)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {isInterventionModalOpen && (
        <InterventionModal
          isOpen={isInterventionModalOpen}
          onClose={handleModalClose}
          onSave={handleSaveIntervention}
          intervention={selectedIntervention}
          patientId={id}
          isLoading={
            createInterventionMutation.isLoading ||
            updateInterventionMutation.isLoading ||
            deleteInterventionMutation.isLoading
          }
        />
      )}
    </div>
  );
};

export default PatientDetail;















